import $ from "jquery";
import {Modal} from 'bootstrap';
import addressMarkerSvg from "./images/address_pin.svg";
import {getPerceivedLightness} from "./color-lightness";
import translations from "./translations.json";
import {labelPoint} from "./label-point";

window.Modal = Modal;

// $targets contains jquery selections for elements on the page to read from, write to, or scope other selectors
// these selectors should match static elements found in index.html and should be considered constant
const $targets = {
    map: $("#map-canvas"),
    overlay: $("#overlay"),
    mainWrapper: $("#main-wrapper"),
    bodyArea: $("#body-area"),
    bodyText: $("#body-text"),
    results: $("#results"),
    filters: $("#results-filter"),
    radius: $("#radius"),
    search: $("#mixed-search"),
    input: $("#mixed-input"),
    addressInput: $("#address-input"),
    schoolInput: $("#school-input"),
    findMe: $("#find-me"),
    reset: $("#reset"),
    translate: $("#translate"),
    dataLoader: $("#data-loader"),
    displayToggle: $("#display-toggle")
};

const color = {
    orange: '#ed7d31',
    red: '#e12c41',
    blue: '#4472c4',
    yellow: '#FFC000',
    green: '#7ec352',
    purple: '#8b2ba1',
    grey: '#a5a5a5',
    light: '#dddddd',
    dark: '#333333',
    darkBlue: '#182844',
    darkRed: '#541018',
    darkGreen: '#2a3f1b',
    darkOrange: '#542a12',
    darkYellow: '#574000'
};

// print a date in local Chicago time
const formattedDateChicago = (d) => d.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    dateStyle: "short",
    timeStyle: "short"
});

const numberWithCommas = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// return an element wrapped in a <strong> tag
const $strong = (elem) => $("<strong>").html(elem);

// display a value as a rounded percent
const percentDisplay = (val) => `${Math.round(Number(val) * 100)}%`;

const chicago = {lat: 41.881, lng: -87.6863}; // real center of the city is madison and western

// Create a bounding box used to set a focus for the address autocomplete menu
// Calculated by using difference between the center and 4 points at the farthest boundaries and rounding up to 3 decimal places
const chicagoBounds = {
    north: chicago.lat + 0.142,
    south: chicago.lat - 0.237,
    east: chicago.lng + 0.162,
    west: chicago.lng - 0.168,
};

// default polygon style
const POLYGON_STYLE = (clickable = false, isHover = false) => ({
    "strokeColor": "#0149DA",
    "strokeOpacity": 1,
    "strokeWeight": isHover ? 5 : 3,
    "fillOpacity": isHover ? 0.5 : 0.2,
    "fillColor": "#0B5394",
    "clickable": clickable,
});

// polygon label. Has default size and width
class PolygonLabel {
    constructor(text, fontSize = "14px", labelWidth = "5em") {
        this.text = text;
        this.fontSize = fontSize;
        this.labelWidth = labelWidth;
    }

    get textElement() {
        const div = document.createElement("div");
        div.innerHTML = this.text;
        div.style.fontSize = this.fontSize;
        div.style.maxWidth = this.labelWidth;
        div.classList.add("map-label");
        return div;
    }
}

// add a single polygon to the map
function showPolygon(map, feature, style = POLYGON_STYLE(), label) {
    // show the feature polygon
    map.data.setStyle(style);
    const polygon = map.data.addGeoJson(feature);

    // create a marker for the label if requested
    if (label && feature) {
        let polyLabel = label;
        if (typeof label === 'string') {
            polyLabel = new PolygonLabel(label);
        }
        const labelMarker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: labelPosition(feature),
            content: polyLabel.textElement,
        });

        // push the label under any marker pins
        labelMarker.zIndex = 0;

        // return the polygon and labelMarker objects so they can be accessed or removed later
        return {polygon: polygon, labelMarker: labelMarker};
    }
}

function labelPosition(feature) {
    // check to see if this is a polygon or multipolygon. Only use the first set of coordinates if it is a multipolygon
    let coordinates;
    if (feature.geometry.type === "Polygon") {
        coordinates = feature.geometry.coordinates[0];
    } else if (feature.geometry.type === "MultiPolygon") {
        coordinates = feature.geometry.coordinates[0][0];
    }

    // return the best label position as a lat/lng location
    if (coordinates) {
        const foundLabelPoint = labelPoint(coordinates);
        return {lng: foundLabelPoint[0], lat: foundLabelPoint[1]};
    }
}

// get the visual center of a set of coordinates (polygon)
function findCenter(coordinates) {
    let totalArea = 0;
    let centerX = 0;
    let centerY = 0;

    for (let i = 0; i < coordinates.length; i++) {
        const current = coordinates[i];
        const next = coordinates[(i + 1) % coordinates.length];
        const area = (current[0] * next[1] - next[0] * current[1]);
        totalArea += area;
        centerX += (current[0] + next[0]) * area;
        centerY += (current[1] + next[1]) * area;
    }

    const factor = 1 / (3 * totalArea);
    centerX *= factor;
    centerY *= factor;

    return [centerX, centerY];
}

// general purpose api calling wrapper
async function getAPIValues(url, data) {
    try {
        return await $.ajax({
            type: "GET",
            url: url,
            dataType: "json",
            data: data
        });
    } catch (error) {
        if (error.responseJSON) {
            if (error.responseJSON.error) {
                alert(`ERROR: ${error.responseJSON.error.message}`);
            }
        }
        console.error(`ERROR: getAPIValues(url, data)`);
        console.error(url, data, error);
    }
}

function $tableScroll($elem) {
    return $("<div>").addClass("scrolling-wrapper").html($elem);
}

function $loader() {
    return $("<div>").addClass("loader-holder").html($("<span>").addClass("loader"));
}

function animateMarker(marker) {
    marker.content.classList.add("bounce");
    marker.zIndex = 999;
}

// switches to the smaller image
function stopAnimateMarker(marker) {
    marker.content.classList.remove("bounce");
    marker.zIndex = 2;
}

// position the markers to the best part of the viewable area
function positionMarkersOnMap(map, $targets) {
    // "true" is a string not a true boolean as it is a URL parameter or html attribute
    if ($targets.displayToggle.attr("aria-expanded") === "true") {
        // centers the markers on the right side of the viewport on larger displays
        if ($(window).width() > $targets.overlay.width()) {
            map.panBy(-$targets.overlay.width() / 2, 0);
        }
        // centers the markers on the middle bottom of the screen on mobile displays
        else if ($(window).height() > $targets.overlay.height()) {
            map.panBy(0, -$targets.overlay.height() / 2);
        }
    }
}

// general purpose function to set map scale
// data can be an array of markers, a polygon, or a featureCollection, or nothing
function setMapBounds(map, data, addressMarker, searchRadiusCircle) {
    const latLngBounds = new google.maps.LatLngBounds();

    if (addressMarker) {
        latLngBounds.extend(addressMarker.position);
    }

    if (searchRadiusCircle) {
        const r = searchRadiusCircle.radius;
        const c = searchRadiusCircle.center;
        const offset = {
            lat: r / (111111),
            lng: r / (111320 * Math.cos(c.lat() * (Math.PI / 180)))
        };
        latLngBounds.extend(new google.maps.LatLng(c.lat() + offset.lat, c.lng() + offset.lng));
        latLngBounds.extend(new google.maps.LatLng(c.lat() - offset.lat, c.lng() - offset.lng));
    }

    if (data && data.type === "FeatureCollection") {
        data.features.forEach(f => {
            if (f.geometry.type === "Polygon") {
                f.geometry.coordinates[0].forEach(c => {
                    latLngBounds.extend(new google.maps.LatLng(c[1], c[0]));
                });
            } else if (f.geometry.type === "MultiPolygon") {
                f.geometry.coordinates.forEach(p => {
                    p[0].forEach(c => {
                        latLngBounds.extend(new google.maps.LatLng(c[1], c[0]));
                    });
                });
            }
        });
        map.fitBounds(latLngBounds);
        //map.setZoom(map.getZoom() - .3); // zoom out a little to make sure entire polygon fits onscreen
    } else if (data && data.type === "Feature") { // this is a Polygon or MultiPolygon
        if (data.geometry.type === "Polygon") {
            data.geometry.coordinates[0].forEach(c => {
                latLngBounds.extend(new google.maps.LatLng(c[1], c[0]));
            });
        } else if (data.geometry.type === "MultiPolygon") {
            data.geometry.coordinates.forEach(p => {
                p[0].forEach(c => {
                    latLngBounds.extend(new google.maps.LatLng(c[1], c[0]));
                });
            });
        }
        map.fitBounds(latLngBounds);
        //map.setZoom(map.getZoom() - .3); // zoom out a little to make sure entire polygon fits onscreen
    } else if (data && data[0]) { //this is a set of markers
        data.forEach(marker => {
            if (marker.map && marker.position) {
                latLngBounds.extend(marker.position);
            }
        });
        map.fitBounds(latLngBounds);
    } else {
        map.panTo(new google.maps.LatLng(chicago.lat, chicago.lng));
        map.setZoom(11);
    }
}

// calculate the distance between a pair of latitude,longitude coordinates taking into account the curve of the earth
function distanceCalc(p1, p2, isMiles = true) {
    // https://stackoverflow.com/questions/14560999/using-the-haversine-formula-in-javascript
    /*
    d = 2R × sin⁻¹(√[sin²((θ₂ - θ₁)/2) + cosθ₁ × cosθ₂ × sin²((φ₂ - φ₁)/2)]).
    where:
    (θ₁, φ₁) and (θ₂, φ₂) – Each point's coordinates;
    R – Earth's radius; and
    d – Great circle or 'as the crow flies' distance between the points.
     */

    function toRad(x) {
        return x * Math.PI / 180;
    }

    const R = 6371; // km
    const dLat = toRad(p2.lat - p1.lat);
    const dLon = toRad(p2.lng - p1.lng);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = R * c;

    if (isMiles) {
        d /= 1.60934;
    }
    return d;
}

// wrapper for the google api key for development
// takes the actual project api key as input and simply returns it if running live
// if developing locally, the development key is returned instead
function googleAPIKey(key) {
    const pageURL = top.location.href;
    if (pageURL.indexOf("file:///") >= 0 || pageURL.indexOf("localhost") >= 0 || pageURL.indexOf("127.0.0.1") >= 0) {
        return ("AIzaSyCQdql9IS1sEw--LLnOD1B8ccfB0uQP6Jw");
    }
    return (key); // this should be unique for every project
}

// replace header and body with data from the CMS
function updateCMSElements($headElement, header, $bodyElement, body) {
    if (header) {
        $headElement.html(header);
    }
    if (body) {
        $bodyElement.html($(body));
    }
}

// replace an element's contents
function updateElement($element, content) {
    if (content) {
        $element.html(content);
    }
}

// return an element with the results table and totals
function $resultTable($header, $body) {
    // create the list of results
    return $("<table>").addClass("table table-striped resultList").html($header).append($body);
}

// given a row in a results table, scroll it into view
function scrollToRow($row, $results) {
    const elem = $row[0];

    if (elem) {
        // scroll to selected row
        elem.scrollIntoView();

        // adjust the scroll position for the floating header
        $results[0].scrollTop -= $("thead", $results).height();

        // scroll the selected row back into view if the previous adjustment pushed it off the bottom (usually last row)
        if (elem.getBoundingClientRect().bottom > window.innerHeight) {
            elem.scrollIntoView();
        }
    }
}

function initModal(title, body) {
    // create the modal element if it doesn't exist yet
    if (!$("#MapModal")[0]) {
        $("body").append($modalTemplate("MapModal"));
    }

    // fill in the modal contents
    $(".modal-title", `#MapModal`).html(title);
    $(".modal-body", `#MapModal`).html(body);

    return $("#MapModal");
}

function $modalTemplate(id) {
    const $title = $("<h5>").addClass("modal-title");
    const $spacer = $("<span>").attr({"aria-hidden": "true"}).html("&nbsp;");
    const $closeButton = $("<button>").addClass("btn-close").attr({
        "type": "button",
        "data-bs-dismiss": "modal",
        "aria-label": "Close"
    }).html($spacer);
    const $modalHeader = $("<div>").addClass("modal-header").append($title, $closeButton);
    const $modalBody = $("<div>").addClass("modal-body");
    const $footerButton = $("<button>").addClass("btn btn-outline-primary").attr({
        "type": "button",
        "data-bs-dismiss": "modal"
    }).html("Close");
    const $modalFooter = $("<div>").addClass("modal-footer").html($footerButton);
    const $modalContent = $("<div>").addClass("modal-content").append($modalHeader, $modalBody, $modalFooter);
    const $modalDialog = $("<div>").addClass("modal-dialog").attr({"role": "document"}).html($modalContent);
    return $("<div>").addClass("modal").attr({
        "id": id,
        "tabindex": "-1",
        "role": "dialog",
        "aria-labelledby": id
    }).html($modalDialog);
}

// return an array of objects keyed on names matching the source names in "headers"
function googleSheetObj(sheet, headers) {
    // get the spreadsheet column numbers based on the text in the header row
    const values = sheet.data[0].rowData;
    const headerIndices = {};
    const sheetObj = [];

    // look at the first row and match the text to index numbers
    values[0].values.some((val, ind) => {
        if (val) {
            if (headers[sheetValue(val)]) {
                headerIndices[headers[sheetValue(val)]] = ind;
            }
        }
    });

    // use the header index numbers from the previous step to add keys to each row in the sheet
    // skip header row - start at 1
    for (let i = 1; i < values.length; ++i) {
        const row = {};
        Object.keys(headerIndices).forEach(key => row[key] = sheetValue(values[i].values[headerIndices[key]]));
        sheetObj.push(row);
    }
    return (sheetObj);

    // returns the value from a cell in a google sheet
    function sheetValue(valueObj) {
        if (valueObj && valueObj.effectiveValue) {
            if (valueObj.effectiveValue.numberValue !== undefined) return valueObj.effectiveValue.numberValue;
            if (valueObj.effectiveValue.stringValue !== undefined) return valueObj.effectiveValue.stringValue;
            return valueObj.formattedValue;
        } else {
            return undefined;
        }
    }
}

function parseTranslations($overlay) {
    const translations = {
        en: {},
        es: {}
    };
    // gather the Spanish translations first. This is in the translations-es block and will be the master list of text available for translation
    const translationsEs = $(".translations-es [data-translation]", $overlay).toArray();

    translationsEs.forEach((elem) => {
        const target = elem.dataset.translation;
        translations["es"][target] = elem.innerHTML.trim();
    });

    // remove the element with Spanish translations now that it has been read
    $(".translations-es", $overlay).remove();

    const translationsEn = $("[data-translation]", $overlay).toArray();
    translationsEn.forEach((elem) => {
        const target = elem.dataset.translation;
        translations["en"][target] = elem.innerHTML.trim();
    });

    return translations;
}

// create a marker for debugging a position
function debugMarker(map, location, options) {
    // create advancedMarker
    const pin = new google.maps.marker.PinElement({
        background: color.red,
        borderColor: color.darkRed,
        scale: .7,
        glyph: "D"
    });
    const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: location,
        title: "DEBUG",
        content: pin.element,
    });
    advancedMarker.zIndex = 2;
    return advancedMarker;
}


// set the address marker based on a location and attach the formatted address string
function setAddressMarker(map, location, formattedAddress) {
    // create pin image
    const pinImg = (new DOMParser()).parseFromString(addressMarkerSvg, 'image/svg+xml').documentElement;
    pinImg.setAttribute("height", 28)

    // create advancedMarker
    const addressMarker = new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: location,
        title: formattedAddress,
        content: pinImg,
    });
    addressMarker.zIndex = 1;
    addressMarker.formattedAddress = formattedAddress;

    // add drop animation
    const content = addressMarker.content;
    content.classList.add("drop");
    content.style.opacity = "0";
    content.addEventListener("animationend", (event) => {
        content.classList.remove("drop");
        content.style.opacity = "1";
    });
    return addressMarker;
}

// return a linked direction icon from a given destination address and optional start address 
function $addressDirectionLink(address, addressMarker) {
    // create directions link
    const startAddr = addressMarker ? `saddr=${addressMarker.formattedAddress}&` : "";
    const destAddr = `daddr=${address}`;
    const $dirLink = $("<a>").attr({
        "data-toggle": "tooltip",
        "title": "Directions",
        "href": `https://maps.google.com/maps?${startAddr}${destAddr}`,
        "target": "_blank"
    });
    const $dirIcon = $("<span>").addClass("fas fa-directions").attr({"aria-hidden": "true"});
    return $dirLink.append(`${address}&nbsp;`, $dirIcon);
}

// general purpose two-column table row for displaying a label and value
function $infoRow(label, val) {
    const $tr = $("<tr>");
    const $label = $("<td>").addClass("table-label").html(label);
    const $value = $("<td>").html(val);
    $tr.append($label, $value);
    return $tr;
}

// make a google geocoder request and provide a callback to process successfull results. errors are displayed in a modal
function geocodeRequest(location, callback) {
    if (location) {
        const request = {}; // used to hold the qooqle search request. .location is used for a lat/long search and .address is used for a street address string

        // A latlng call can be handled by google, no point in running it through ESRI
        if (location.lat) {
            request.location = location;
        }

        // not a latLng value - use a street address
        else {
            let editedAddress = location.split(',')[0];
            if (editedAddress.toLowerCase().indexOf("chicago") === -1) {
                editedAddress += " chicago, illinois";
            }
            request.address = editedAddress;
        }

        const googleGeocoder = new google.maps.Geocoder();
        googleGeocoder.geocode(request, (results, status) => callback(results, status, request));
    } else {
        const $modal = initModal("Enter an address", $("<p>").html("Please enter an address."));
        const myModal = new Modal($modal);
        myModal.show();
        //alert("Enter an address");
    }
}

const metersPerMile = 1609.344;

// draw a circle on the map the size of searchRadius
function drawSearchRadiusCircle(map, location, radiusMeters, searchRadiusCircle) {
    if (searchRadiusCircle) {
        searchRadiusCircle.setMap(null);
    }
    const circleOptions = {
        strokeColor: "#4b58a6",
        strokeOpacity: 0.3,
        strokeWeight: 1,
        fillColor: "#4b58a6",
        fillOpacity: 0.05,
        map: map,
        center: location,
        clickable: false,
        zIndex: -1,
        radius: radiusMeters
    };
    return new google.maps.Circle(circleOptions);
}

// given a background color, return a light or dark contrasting color for text
function textContrast(c) {
    return getPerceivedLightness(c) < 70 ? color.light : color.dark
}

//add selection highlighting to jquery ui autocomplete menu
function menuHighlight(ul, item) {
    return $("<li>").data("item.autocomplete", item).append($("<div>").html(String(item.label).replace(new RegExp(this.term, "gi"), "<span class='ui-highlight'>$&</span>"))).appendTo(ul);
}

// check for exact then partial matches in an array of objects based on a given key and return the first match
function inputSubstringMatch(input, arr, key) {
    // check for an exact match on the input
    const exactMatch = arr.find((item) => item[key].toUpperCase() === input.toUpperCase());
    if (exactMatch) {
        return exactMatch;
    }

    // check for a match at the beginning
    const startMatch = arr.find((item) => item[key].toUpperCase().startsWith(input.toUpperCase()));
    if (startMatch) {
        return startMatch;
    }

    // There isn't a match for the beginning of the string. Check to see if the substring occurs anywhere
    const anyMatch = arr.filter((item) => item[key].toUpperCase().includes(input.toUpperCase()));
    if (anyMatch.length !== 0) {
        anyMatch.sort((a, b) => a[key].localeCompare(b[key]));
        return anyMatch[0];
    } else {
        return false;
    }
}

// replace whitespace with underscores in string
const toSnakeCase = (str) => {
    return str && str
        .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
        .map(x => x.toLowerCase())
        .join('_');
};

// find elements on the page that should be translated and update their text accordingly 
function translatePage(lang, $targets) {
    // set the page language attribute
    $("html").attr("lang", lang);

    // replace page text with translations
    $("[data-translation]", $targets.overlay).each(function () {
        $(this).html(translations[lang][$(this).attr("data-translation")]);
    });

    // pick up the input placeholder edge case
    $targets.input.attr("placeholder", translations[lang]["placeholder-search-address-school"]);
    $targets.schoolInput.attr("placeholder", translations[lang]["placeholder-search-school"]);
    $targets.addressInput.attr("placeholder", translations[lang]["placeholder-search-address"]);
}

export {
    animateMarker,
    distanceCalc,
    drawSearchRadiusCircle,
    findCenter,
    formattedDateChicago,
    geocodeRequest,
    getAPIValues,
    googleAPIKey,
    googleSheetObj,
    initModal,
    inputSubstringMatch,
    labelPosition,
    menuHighlight,
    numberWithCommas,
    parseTranslations,
    percentDisplay,
    positionMarkersOnMap,
    scrollToRow,
    setAddressMarker,
    setMapBounds,
    showPolygon,
    stopAnimateMarker,
    textContrast,
    toSnakeCase,
    translatePage,
    updateCMSElements,
    updateElement,
    PolygonLabel,
    POLYGON_STYLE,
    $addressDirectionLink,
    $infoRow,
    $loader,
    $resultTable,
    $strong,
    $tableScroll,
    $targets,
    chicago,
    chicagoBounds,
    color,
    metersPerMile,
};
